/**
 * `/mail` (index) — Inbox pane of the mail suite.
 *
 * The parent layout in `routes/mail.tsx` provides AdminShell + the
 * Mail / Calendar sub-nav. This file just renders the mail surface
 * inside the parent's <Outlet />.
 *
 * Cross-feature: the "📅 Create event" toolbar button writes a draft
 * to sessionStorage and navigates to `/mail/calendar`. The Calendar
 * pane consumes the draft on mount + auto-opens the form prefilled
 * with subject → title, participants → attendees, snippet →
 * description.
 *
 * Joint-project note: `<Mail>` itself lives in `@postopia/mail-ui`
 * and is consumed by MyVigator's bundle too. This file only wires
 * the Postopia-side bridge.
 */

import { useEffect, useMemo, useState } from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { Mail } from '@postopia/mail-ui';
import type { MailAccount, Message, Thread } from '@postopia/mail-ui';
import { setPendingEventDraft } from '../lib/cross-feature';
import { postopiaMailDataSource } from '../lib/mailDataSource';
import { makeImpersonatingDataSource } from '../lib/impersonatingDataSource';
import { ActingAsBanner, MailboxPicker } from '../components/ActingAsChrome';
import { mailApi } from '../lib/mailApi';

/** localStorage key for the acting-as selection — MyVigator's
 *  IMPERSONATION_KEY pattern, so a reload keeps you in the mailbox
 *  you were triaging. */
const ACTING_KEY = 'q.mail.actingAs';

export const Route = createFileRoute('/mail/')({
  component: InboxPane,
});

function InboxPane() {
  const navigate = useNavigate();

  /* ── Acting-as (2026-08-30, ported from MyVigatorMail.jsx) ───────────
   * The whole surface re-scopes to the selected account: the dataSource
   * is SWAPPED (see impersonatingDataSource.ts for why) and <Mail> is
   * re-mounted keyed on the account id so folder/thread/search state
   * resets cleanly — MyVigator's exact comment applies. */
  const [actingAs, setActingAs] = useState<MailAccount | null>(() => {
    try {
      const raw = localStorage.getItem(ACTING_KEY);
      return raw ? (JSON.parse(raw) as MailAccount) : null;
    } catch {
      return null;
    }
  });
  useEffect(() => {
    try {
      if (actingAs) localStorage.setItem(ACTING_KEY, JSON.stringify(actingAs));
      else localStorage.removeItem(ACTING_KEY);
    } catch {
      /* private mode */
    }
  }, [actingAs]);

  // Deep link: /mail?accountId=<uuid> (the launchpad's Shared-mailboxes
  // tile, or any "open this mailbox" link). Consumed once, then stripped
  // so refresh/back doesn't re-trigger — MyVigator's param dance.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const accountId = params.get('accountId');
    if (!accountId) return;
    void (async () => {
      try {
        const resp = await mailApi.searchAccounts('', { limit: 200 });
        const hit = resp.items.find((a) => a.id === accountId);
        if (hit) {
          setActingAs({
            id: hit.id,
            name: hit.display_name ?? hit.local_part,
            email: hit.primary_email,
            local_part: hit.local_part,
            candidate_id: hit.candidate_id,
            org_id: hit.org_id,
            owner_unread_count: hit.owner_unread_count,
            admin_unread_count: hit.admin_unread_count,
            last_message_at: hit.last_message_at,
          });
        }
      } finally {
        params.delete('accountId');
        const qs = params.toString();
        window.history.replaceState(
          null,
          '',
          `${window.location.pathname}${qs ? `?${qs}` : ''}`,
        );
      }
    })();
  }, []);

  const activeDataSource = useMemo(
    () => (actingAs ? makeImpersonatingDataSource(actingAs) : postopiaMailDataSource),
    [actingAs],
  );
  // Mobile bottom-tab nav previously lived here as a `mobileBottomNav`
  // prop on <Mail>. Moved to the parent layout (`routes/mail.tsx →
  // MobileSuiteNav`) so the same bar shows on Calendar too without
  // needing the prop on each pane.

  function handleCreateEvent(thread: Thread, latestMessage: Message) {
    // Attendees = union of from + to from the latest message, deduped
    // case-insensitively, capped to keep wide-distribution threads
    // sane.
    const attendeesByEmail = new Map<string, { email: string; name?: string }>();
    function add(r: { email: string; name?: string }) {
      const key = r.email.toLowerCase();
      if (attendeesByEmail.has(key)) return;
      attendeesByEmail.set(
        key,
        r.name ? { email: r.email, name: r.name } : { email: r.email },
      );
    }
    add({ email: latestMessage.from.email, name: latestMessage.from.name });
    for (const t of latestMessage.to) add({ email: t.email, name: t.name });
    const attendees = Array.from(attendeesByEmail.values()).slice(0, 20);

    setPendingEventDraft({
      title: thread.subject,
      description: thread.snippet,
      attendees,
      source: 'From mail',
    });
    void navigate({ to: '/mail/calendar' });
  }

  return (
    <>
      <div
        style={{
          display: 'flex',
          justifyContent: 'flex-end',
          padding: '6px 12px 0',
        }}
      >
        <MailboxPicker selected={actingAs} onSelect={setActingAs} />
      </div>
      {actingAs ? (
        <ActingAsBanner account={actingAs} onStop={() => setActingAs(null)} />
      ) : null}
      <Mail
        // Re-mount on scope switch so internal folder/thread/search state
        // resets — keying on the account id is the simplest signal
        // (MyVigatorMail.jsx, verbatim rationale).
        key={actingAs?.id ?? 'self'}
        onCreateEvent={handleCreateEvent}
        dataSource={activeDataSource}
      />
    </>
  );
}
